/**
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2020 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers.  The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */

/**
 * Override this handler to parse the product documents to find product videos
 * Override this handler to add some custom expands to load-Product request
 */

import setProductVideos from "@eisinc/App/Handlers/Pages/ProductDetails/SetProductVideos";
import { AttributeGroup, DocumentGroup, VideoDocumentModel } from "@eisinc/App/Store/Reducers/ProductDetails/state";
import { Dictionary } from "@insite/client-framework/Common/Types";
import { Handler, HasOnSuccess, replaceInChain } from "@insite/client-framework/HandlerCreator";
import { GetProductByPathApiV2Parameter } from "@insite/client-framework/Services/ProductServiceV2";
import {
    chain,
    DispatchCompleteLoadProduct,
    PopulateApiParameter,
} from "@insite/client-framework/Store/Data/Products/Handlers/LoadProductByPath";
import { ProductModel } from "@insite/client-framework/Types/ApiModels";
import cloneDeep from "lodash/cloneDeep";

type Parameter = {
    path: string;
    addToRecentlyViewed?: boolean;
} & HasOnSuccess<ProductModel>;
type Props = {
    apiParameter: GetProductByPathApiV2Parameter;
    apiResult: ProductModel;
};

type HandlerType = Handler<Parameter, Props>;

const videoDocumentType = "video";

function getVideosDocuments(product: ProductModel) {
    return product.documents?.filter(doc => doc.documentType.toLowerCase() === videoDocumentType);
}

export const CustomPopulateApiParameter: HandlerType = props => {
    props.apiParameter = {
        ...props.parameter,
        expand: [
            "detail",
            "specifications",
            "content",
            "images",
            "documents",
            "attributes",
            "variantTraits",
            "badges",
            // <xng-change>
            "properties",
            "getbadges" as any,
            // </xng-change>
        ],
        additionalExpands: ["getstyledchildrencount"],
        includeAttributes: ["includeOnProduct", "notFromCategory"],
    };
};

function parseProductVideos(product: ProductModel): VideoDocumentModel[] {
    const documentVideos = getVideosDocuments(product);
    const videos: VideoDocumentModel[] = documentVideos && documentVideos?.length > 0 ? cloneDeep(documentVideos) : [];
    videos.every(video => {
        const videoId = video.filePath && video.filePath.substr(video.filePath.lastIndexOf("/") + 1);
        video.thumbnailPath = `https://img.youtube.com/vi/${videoId}/default.jpg`;
        return true;
    });
    return videos;
}

function parseProductAttributesGroups(product: ProductModel): AttributeGroup[] {
    const groupsString = product.properties.groupingAttributes;
    if (groupsString) {
        return JSON.parse(groupsString);
    }
    return [];
}

function parseProductDocuments({ documents }: ProductModel): DocumentGroup[] {
    const dictionary: Dictionary<DocumentGroup> = {};
    const documentGroups: DocumentGroup[] = [];
    documents?.every(doc => {
        if (doc.documentType.toLowerCase() !== videoDocumentType) {
            let docGroup = dictionary[doc.documentType];
            if (!docGroup) {
                docGroup = dictionary[doc.documentType] = {
                    groupName: doc.documentType,
                    documents: [],
                };
                documentGroups.push(docGroup);
            }
            docGroup.documents.push(doc);
        }
        return true;
    });
    return documentGroups;
}

export function parseProductCustomState(product: ProductModel) {
    const videos = parseProductVideos(product);
    const attributeGroups = parseProductAttributesGroups(product);
    const documentGroups = parseProductDocuments(product);
    let selectedVideoIndex = -1;
    if (videos && videos.length > 0 && product?.images?.length === 0) {
        selectedVideoIndex = 0;
    }

    return {
        customProductState: {
            productId: product.id,
            videos,
            selectedVideoIndex,
            attributeGroups,
            documentGroups,
            properties: product.properties,
        },
    };
}

export const CustomDispatchCompleteLoadProduct: HandlerType = props => {
    if (!props.apiResult) {
        return;
    }

    props.dispatch(setProductVideos(parseProductCustomState(props.apiResult)));

    return DispatchCompleteLoadProduct(props);
};

replaceInChain(chain, DispatchCompleteLoadProduct, CustomDispatchCompleteLoadProduct);
replaceInChain(chain, PopulateApiParameter, CustomPopulateApiParameter);
